/**
 * "How Maverick Works" — beta-user-facing legibility surface.
 *
 * Single purpose: answer "what is this thing actually monitoring, and what
 * can it do?" without operator guesswork.
 *
 * All content sourced from getMaverickCapabilities() in capabilities.ts.
 * Live state read from runtime context. No hand-authored scenario text.
 *
 * Visible in ALL builds (this is a trust surface, not a debug surface).
 */

import { useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info, Eye, Zap, ArrowRight } from "lucide-react";
import {
  getCapabilitiesByCategory,
  CATEGORY_LABELS,
  CLOSE_MODE_LABELS,
  type CapabilityCategory,
} from "@/data/maverick/capabilities";

interface Props {
  /** Live runtime — number of jobs the pre-mortem engine swept this render. */
  evaluatedCount: number;
  /** Number of trend signals below action threshold (visible in Show all). */
  suppressedCount: number;
  /** Currently surfaced primary prediction count (0 or 1 in One Thing mode). */
  primaryActiveCount: number;
}

export function MaverickHowItWorksDrawer({
  evaluatedCount,
  suppressedCount,
  primaryActiveCount,
}: Props) {
  const grouped = useMemo(() => getCapabilitiesByCategory(), []);
  const categories = Object.keys(grouped) as CapabilityCategory[];

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1.5"
        >
          <Info className="h-3 w-3" />
          How Maverick works
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />
            How Maverick works
          </SheetTitle>
          <SheetDescription>
            Maverick is a loop-closure engine. It watches your active work and
            surfaces one thing at a time — only when confidence is high enough
            to act.
          </SheetDescription>
        </SheetHeader>

        {/* ── Live state snapshot ── */}
        <section className="mt-6 rounded-lg border bg-muted/20 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Right now
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <StatBlock label="Monitoring" value={evaluatedCount} suffix="jobs" />
            <StatBlock label="Surfaced" value={primaryActiveCount} suffix="primary" />
            <StatBlock label="Below threshold" value={suppressedCount} suffix="trends" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
            Silence is a valid answer. Maverick stays quiet until a signal
            crosses its confidence floor.
          </p>
        </section>

        {/* ── What Maverick can detect ── */}
        <section className="mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            What it watches for
          </div>
          <div className="space-y-4">
            {categories.map((cat) => (
              <div key={cat}>
                <div className="text-xs font-semibold mb-2">{CATEGORY_LABELS[cat]}</div>
                <ul className="space-y-2">
                  {grouped[cat].map((cap) => (
                    <li
                      key={cap.key}
                      className="rounded-md border bg-card p-3 text-xs"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-medium text-foreground">{cap.name}</div>
                        {!cap.primarySurface && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1.5 shrink-0">
                            trend only
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {cap.description}
                      </p>
                      <div className="flex items-center gap-1 mt-1.5 text-[10px] text-muted-foreground/80">
                        <ArrowRight className="h-2.5 w-2.5" />
                        {CLOSE_MODE_LABELS[cap.closeMode]}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ── How to read the surface ── */}
        <section className="mt-6 rounded-lg border bg-muted/20 p-4 text-xs space-y-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Reading the surface
          </div>
          <div className="flex items-start gap-2">
            <Eye className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <span className="font-medium">Watching</span>
              <span className="text-muted-foreground"> — engine quiet, no item above threshold.</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Zap className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
            <div>
              <span className="font-medium">One Thing</span>
              <span className="text-muted-foreground"> — the highest-confidence prediction with a pre-drafted intervention.</span>
            </div>
          </div>
          <div className="text-muted-foreground pl-5 leading-relaxed">
            "Show all" reveals trend signals that don't warrant the primary
            slot but stay visible for awareness.
          </div>
        </section>
      </SheetContent>
    </Sheet>
  );
}

function StatBlock({ label, value, suffix }: { label: string; value: number; suffix: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
        {label}
      </div>
      <div className="text-[10px] text-muted-foreground/70">{suffix}</div>
    </div>
  );
}
